import { EstadoExpediente } from "./enums";

/**
 * Tabla declarativa de transiciones válidas del workflow.
 *
 * Esta es la fuente de verdad de la máquina de estados. Cualquier transición
 * no listada aquí debe ser rechazada por el backend. Los guards documentan
 * las precondiciones que deben evaluarse antes de aplicar la transición;
 * la evaluación real ocurre en `StateMachineService` (backend).
 *
 * Ver `analisis_sistema_tesis_unheval.md` §8.2 para el detalle de cada
 * transición y sus reglas de negocio.
 */

export type GuardKey =
  // Handshake estudiante-asesor (Fase 6 — ADR 008)
  | "estudiante_capturo_propuesta"
  | "asesor_propuesto_activo"
  | "asesor_acepto_asesoria"
  | "asesor_rechazo_asesoria_con_motivo"
  | "aceptacion_asesor_vigente"
  | "documentos_solicitud_completos"
  | "datos_solicitante_validos"
  | "resolucion_designacion_asesor_emitida"
  | "asesor_activo"
  | "decanato_registro_observaciones"
  | "subsano_solicitud"
  | "notificacion_asesor_enviada"
  | "informe_conformidad_asesor_firmado"
  | "version_proyecto_adjunta"
  | "asesor_registro_observaciones"
  | "resolucion_designacion_jurado_emitida"
  // ADR 028 (revierte ADR 027) — alineado al reglamento UNHEVAL 2025
  // (Art. 32°a + Disp. Compl. SEXTA): el jurado tiene 5 miembros fijos
  // (3 titulares PSV + 2 accesitarios suplentes), uno solo por expediente.
  | "jurado_completo_5_miembros"
  | "roles_jurado_asignados"
  | "notificacion_jurado_enviada"
  | "informe_colegiado_observado"
  | "informe_colegiado_aprobado"
  | "resolucion_aprobacion_proyecto_emitida"
  | "version_proyecto_corregida"
  | "asesor_renovo_conformidad"
  | "fecha_limite_18_meses_calculada"
  | "borrador_adjunto_con_aprobacion_asesor"
  | "dentro_rango_2_a_18_meses"
  | "dictamen_insuficiente"
  | "dictamen_suficiente_en_15_dias_habiles"
  | "subsanacion_dentro_30_dias_calendario"
  | "nueva_version_borrador_adjunta"
  | "solicitud_analisis_antiplagio_registrada"
  | "similitud_supera_umbral"
  | "similitud_bajo_umbral"
  | "archivo_reporte_adjunto"
  | "fecha_solicitud_dentro_6_meses"
  | "resolucion_fecha_con_5_dias_anticipacion"
  | "asistencia_registrada"
  | "promedio_mayor_o_igual_14"
  | "promedio_menor_14"
  | "actas_individuales_emitidas"
  | "version_final_con_hash"
  | "expediente_final_consolidado"
  | "aprobacion_instancias_academicas_registrada";

export interface Transition {
  readonly from: EstadoExpediente;
  readonly to: EstadoExpediente;
  readonly guards: readonly GuardKey[];
}

export const TRANSITIONS: readonly Transition[] = [
  // ───────── Handshake estudiante-asesor (Fase 6 — ADR 008) ─────────
  {
    from: EstadoExpediente.BORRADOR_SOLICITUD,
    to: EstadoExpediente.ASESORIA_SOLICITADA,
    guards: ["estudiante_capturo_propuesta", "asesor_propuesto_activo"],
  },
  {
    from: EstadoExpediente.ASESORIA_SOLICITADA,
    to: EstadoExpediente.ASESORIA_ACEPTADA,
    guards: ["asesor_acepto_asesoria"],
  },
  {
    // El asesor rechaza la propuesta; el expediente vuelve al inicio
    // y el estudiante puede reintentar con otro asesor sin recrearlo.
    from: EstadoExpediente.ASESORIA_SOLICITADA,
    to: EstadoExpediente.BORRADOR_SOLICITUD,
    guards: ["asesor_rechazo_asesoria_con_motivo"],
  },
  {
    from: EstadoExpediente.ASESORIA_ACEPTADA,
    to: EstadoExpediente.SOLICITUD_PRESENTADA,
    guards: ["aceptacion_asesor_vigente", "datos_solicitante_validos"],
  },
  // Transición legacy preservada para retro-compatibilidad con expedientes
  // creados antes de Fase 6 (los que ya estaban en BORRADOR_SOLICITUD
  // pueden saltar directo al modelo viejo si tienen los documentos).
  {
    from: EstadoExpediente.BORRADOR_SOLICITUD,
    to: EstadoExpediente.SOLICITUD_PRESENTADA,
    guards: ["documentos_solicitud_completos", "datos_solicitante_validos"],
  },
  {
    from: EstadoExpediente.SOLICITUD_PRESENTADA,
    to: EstadoExpediente.SOLICITUD_EN_REVISION_DECANATO,
    guards: [],
  },
  {
    from: EstadoExpediente.SOLICITUD_EN_REVISION_DECANATO,
    to: EstadoExpediente.SOLICITUD_OBSERVADA,
    guards: ["decanato_registro_observaciones"],
  },
  {
    from: EstadoExpediente.SOLICITUD_EN_REVISION_DECANATO,
    to: EstadoExpediente.ASESOR_DESIGNADO,
    guards: ["resolucion_designacion_asesor_emitida", "asesor_activo"],
  },
  {
    from: EstadoExpediente.SOLICITUD_OBSERVADA,
    to: EstadoExpediente.SOLICITUD_PRESENTADA,
    guards: ["subsano_solicitud"],
  },
  {
    from: EstadoExpediente.ASESOR_DESIGNADO,
    to: EstadoExpediente.PROYECTO_EN_ELABORACION,
    guards: ["notificacion_asesor_enviada"],
  },
  // ADR 019: el camino directo PROYECTO_EN_ELABORACION → CONFORMIDAD se reemplaza
  // por un rebote explícito con un estado intermedio PROYECTO_EN_REVISION_ASESOR.
  // El estudiante envía a revisión; el asesor decide: conformidad u observa.
  {
    from: EstadoExpediente.PROYECTO_EN_ELABORACION,
    to: EstadoExpediente.PROYECTO_EN_REVISION_ASESOR,
    guards: ["version_proyecto_adjunta"],
  },
  {
    from: EstadoExpediente.PROYECTO_EN_REVISION_ASESOR,
    to: EstadoExpediente.PROYECTO_CON_CONFORMIDAD_ASESOR,
    guards: ["informe_conformidad_asesor_firmado", "version_proyecto_adjunta"],
  },
  {
    // Asesor observa → expediente vuelve a elaboración con observaciones
    // registradas en EventoAuditoria. El loop es ilimitado.
    from: EstadoExpediente.PROYECTO_EN_REVISION_ASESOR,
    to: EstadoExpediente.PROYECTO_EN_ELABORACION,
    guards: ["asesor_registro_observaciones"],
  },
  {
    from: EstadoExpediente.PROYECTO_CON_CONFORMIDAD_ASESOR,
    to: EstadoExpediente.JURADO_DESIGNADO,
    guards: [
      "resolucion_designacion_jurado_emitida",
      // ADR 028 — 5 miembros (3 titulares PSV + 2 accesitarios), reglamento UNHEVAL Art. 32°a.
      "jurado_completo_5_miembros",
      "roles_jurado_asignados",
    ],
  },
  {
    from: EstadoExpediente.JURADO_DESIGNADO,
    to: EstadoExpediente.PROYECTO_EN_REVISION_JURADO,
    guards: ["notificacion_jurado_enviada"],
  },
  {
    from: EstadoExpediente.PROYECTO_EN_REVISION_JURADO,
    to: EstadoExpediente.PROYECTO_OBSERVADO,
    guards: ["informe_colegiado_observado"],
  },
  {
    from: EstadoExpediente.PROYECTO_EN_REVISION_JURADO,
    to: EstadoExpediente.PROYECTO_APROBADO,
    guards: ["informe_colegiado_aprobado", "resolucion_aprobacion_proyecto_emitida"],
  },
  {
    from: EstadoExpediente.PROYECTO_OBSERVADO,
    to: EstadoExpediente.PROYECTO_EN_REVISION_JURADO,
    guards: ["version_proyecto_corregida", "asesor_renovo_conformidad"],
  },
  {
    from: EstadoExpediente.PROYECTO_APROBADO,
    to: EstadoExpediente.TESIS_EN_EJECUCION,
    guards: ["fecha_limite_18_meses_calculada"],
  },
  {
    from: EstadoExpediente.TESIS_EN_EJECUCION,
    to: EstadoExpediente.BORRADOR_TESIS_PRESENTADO,
    guards: ["borrador_adjunto_con_aprobacion_asesor", "dentro_rango_2_a_18_meses"],
  },
  {
    /**
     * Transición sistémica disparada por el cron de plazos (Fase 2 §F2-005)
     * cuando el expediente lleva ≥18 meses en `TESIS_EN_EJECUCION` sin que
     * el estudiante haya presentado el borrador.
     */
    from: EstadoExpediente.TESIS_EN_EJECUCION,
    to: EstadoExpediente.PLAZO_EJECUCION_VENCIDO,
    guards: [],
  },
  // ADR 028 (revierte ADR 027) — Reusamos el mismo Jurado Evaluador del
  // proyecto para dictaminar la suficiencia del borrador. Art. 40° del
  // reglamento UNHEVAL 2025: "Los miembros del Jurado Evaluador tienen
  // la responsabilidad de dictaminar..." — el artículo definido "el"
  // implica continuidad. No hay re-designación obligatoria entre
  // proyecto e informe (eso solo aplica como cambio excepcional si un
  // miembro incumple el plazo de 15 días hábiles, modelado aparte).
  {
    from: EstadoExpediente.BORRADOR_TESIS_PRESENTADO,
    to: EstadoExpediente.BORRADOR_EN_REVISION_JURADO,
    guards: ["notificacion_jurado_enviada"],
  },
  {
    from: EstadoExpediente.BORRADOR_EN_REVISION_JURADO,
    to: EstadoExpediente.BORRADOR_OBSERVADO,
    guards: ["dictamen_insuficiente"],
  },
  {
    from: EstadoExpediente.BORRADOR_EN_REVISION_JURADO,
    to: EstadoExpediente.BORRADOR_SUFICIENTE,
    guards: ["dictamen_suficiente_en_15_dias_habiles"],
  },
  {
    from: EstadoExpediente.BORRADOR_OBSERVADO,
    to: EstadoExpediente.BORRADOR_SUBSANADO,
    guards: ["subsanacion_dentro_30_dias_calendario"],
  },
  {
    from: EstadoExpediente.BORRADOR_SUBSANADO,
    to: EstadoExpediente.BORRADOR_EN_REVISION_JURADO,
    guards: ["nueva_version_borrador_adjunta"],
  },
  {
    from: EstadoExpediente.BORRADOR_SUFICIENTE,
    to: EstadoExpediente.EN_CONTROL_ANTIPLAGIO,
    guards: ["solicitud_analisis_antiplagio_registrada"],
  },
  {
    from: EstadoExpediente.EN_CONTROL_ANTIPLAGIO,
    to: EstadoExpediente.ANTIPLAGIO_OBSERVADO,
    guards: ["similitud_supera_umbral"],
  },
  {
    from: EstadoExpediente.EN_CONTROL_ANTIPLAGIO,
    to: EstadoExpediente.ANTIPLAGIO_APROBADO,
    guards: ["similitud_bajo_umbral", "archivo_reporte_adjunto"],
  },
  {
    from: EstadoExpediente.ANTIPLAGIO_OBSERVADO,
    to: EstadoExpediente.BORRADOR_OBSERVADO,
    guards: [],
  },
  {
    from: EstadoExpediente.ANTIPLAGIO_APROBADO,
    to: EstadoExpediente.FECHA_SUSTENTACION_SOLICITADA,
    guards: ["fecha_solicitud_dentro_6_meses"],
  },
  {
    from: EstadoExpediente.FECHA_SUSTENTACION_SOLICITADA,
    to: EstadoExpediente.SUSTENTACION_PROGRAMADA,
    guards: ["resolucion_fecha_con_5_dias_anticipacion"],
  },
  {
    from: EstadoExpediente.SUSTENTACION_PROGRAMADA,
    to: EstadoExpediente.SUSTENTACION_REALIZADA,
    guards: ["asistencia_registrada"],
  },
  {
    from: EstadoExpediente.SUSTENTACION_REALIZADA,
    to: EstadoExpediente.SUSTENTACION_APROBADA,
    guards: ["promedio_mayor_o_igual_14", "actas_individuales_emitidas"],
  },
  {
    from: EstadoExpediente.SUSTENTACION_REALIZADA,
    to: EstadoExpediente.SUSTENTACION_DESAPROBADA,
    guards: ["promedio_menor_14"],
  },
  {
    from: EstadoExpediente.SUSTENTACION_APROBADA,
    to: EstadoExpediente.TESIS_REGISTRADA_REPOSITORIO,
    guards: ["version_final_con_hash"],
  },
  {
    from: EstadoExpediente.TESIS_REGISTRADA_REPOSITORIO,
    to: EstadoExpediente.TITULO_EN_TRAMITE,
    guards: ["expediente_final_consolidado"],
  },
  {
    from: EstadoExpediente.TITULO_EN_TRAMITE,
    to: EstadoExpediente.TITULO_OTORGADO,
    guards: ["aprobacion_instancias_academicas_registrada"],
  },
];

export function isValidTransition(from: EstadoExpediente, to: EstadoExpediente): boolean {
  return TRANSITIONS.some((t) => t.from === from && t.to === to);
}

export function getTransition(
  from: EstadoExpediente,
  to: EstadoExpediente,
): Transition | undefined {
  return TRANSITIONS.find((t) => t.from === from && t.to === to);
}

export function getReachableStates(from: EstadoExpediente): EstadoExpediente[] {
  return TRANSITIONS.filter((t) => t.from === from).map((t) => t.to);
}

export function getIncomingStates(to: EstadoExpediente): EstadoExpediente[] {
  return TRANSITIONS.filter((t) => t.to === to).map((t) => t.from);
}
